<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center">
        <wz-button variant="light" class="mr-3" @click="$router.push({ name: 'contracts-list' })">
          <i class="ri-arrow-left-line mr-1"></i>Volver
        </wz-button>
        <h1 class="wz-page-title mb-0">Detalle de contrato {{ contract ? contract.number : '' }}</h1>
      </div>
      <div v-if="contract" class="flex items-center">
        <wz-button
          variant="outline"
          size="sm"
          class="mr-3"
          @click="goToAuditHistory"
        >
          <i class="ri-history-line mr-1"></i>Ver historial de auditoría
        </wz-button>
        <wz-badge :variant="statusBadge.color" pill>{{ statusBadge.label }}</wz-badge>
        <wz-badge v-if="contract.hasRegisteredRenewal" variant="success" pill class="ml-1">RENOVADO</wz-badge>
      </div>
    </div>

    <template v-if="contract">
      <!-- Banner de renovación: este contrato (B) es la renovación de un contrato origen (A). -->
      <wz-alert v-if="contract.renewFromId" variant="info" icon="ri-information-line" class="mb-3">
        Este contrato es la renovación del contrato <strong>{{ contract.renewFromNumber }}</strong>
        vigente del <strong v-local-date="contract.renewFromStartDate"></strong>
        al <strong v-local-date="contract.renewFromFinishDate"></strong>.
        <a href="#" class="font-medium" @click.prevent="goToOriginContract">Ver contrato anterior</a>
      </wz-alert>

      <!-- Sección 1: Datos del contrato -->
      <wz-card title="Datos del contrato" class="mb-3">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Colaborador</small>
            <strong>{{ employeeName }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Tipo de contrato</small>
            <strong>{{ contractTypeLabel }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Duración</small>
            <strong>{{ durationLabel }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Fecha de inicio</small>
            <strong v-local-date="contract.startDate"></strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Fecha de fin</small>
            <strong v-if="contract.finishDate" v-local-date="contract.finishDate"></strong>
            <strong v-else>Indeterminado</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Fin período de prueba</small>
            <strong v-local-date="contract.probationEndDate"></strong>
          </div>
        </div>
      </wz-card>

      <!-- Sección 2: Ubicación laboral -->
      <wz-card title="Ubicación laboral" class="mb-3">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Empresa</small>
            <strong>{{ refName(contract.company, 'name') }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Sede</small>
            <strong>{{ workSiteName }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Área</small>
            <strong>{{ refName(contract.area, 'description') }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Cargo</small>
            <strong>{{ refName(contract.position, 'description') }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Jefe directo</small>
            <strong>{{ refName(contract.directManager, 'description') }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Horario</small>
            <strong>{{ refName(contract.schedule, 'name') }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Canal</small>
            <strong>{{ contract.channelName || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Línea de gasto</small>
            <strong>{{ contract.expenseLineName || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Responsable de RRHH</small>
            <strong>{{ contract.hrResponsibleEmail || '---' }}</strong>
          </div>
        </div>
      </wz-card>

      <!-- Sección 3: Condiciones -->
      <wz-card title="Condiciones" class="mb-3">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Sueldo base</small>
            <strong>{{ formatSalary }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Moneda</small>
            <strong>{{ contract.salaryCurrency || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Régimen laboral</small>
            <strong>{{ contract.laborRegime || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Categoría ocupacional</small>
            <strong>{{ contract.occupationalCategory || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Jornada</small>
            <strong>{{ contract.workSchedule || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Horas semanales</small>
            <strong>{{ contract.weeklyHours != null ? contract.weeklyHours : '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Asignación familiar</small>
            <strong>{{ boolLabel(contract.hasFamilyAllowance) }}<span
              v-if="contract.hasFamilyAllowance && familyAllowanceLabel" class="text-muted"> ({{ familyAllowanceLabel }})</span></strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Correo corporativo</small>
            <strong>{{ contract.corporateEmail || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Teléfono corporativo</small>
            <strong>{{ contract.corporatePhone || '---' }}</strong>
          </div>
          <div class="w-full px-2 mb-3">
            <small class="text-muted block">Tipos de planilla</small>
            <template v-if="contract.payrollTypeNames && contract.payrollTypeNames.length">
              <wz-badge v-for="(name, idx) in contract.payrollTypeNames" :key="idx" variant="info-solid" class="mr-1">
                {{ name }}
              </wz-badge>
            </template>
            <strong v-else>---</strong>
          </div>
        </div>
      </wz-card>

      <!-- Sección 4: Seguros y salud -->
      <wz-card title="Seguros y salud" class="mb-3">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Código EsSalud</small>
            <strong>{{ contract.essaludCode || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">EPS</small>
            <strong>{{ boolLabel(contract.hasEps) }}<span v-if="contract.epsEntity"> ({{ contract.epsEntity }})</span></strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">SCTR Pensión</small>
            <strong>{{ boolLabel(contract.hasSctrPension) }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">SCTR Salud</small>
            <strong>{{ boolLabel(contract.hasSctrHealth) }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Seguro de vida ley</small>
            <strong>{{ boolLabel(contract.hasLifeInsurance) }}</strong>
          </div>
        </div>
      </wz-card>

      <!-- Adendas del contrato (aplica a cualquier contrato).
           highlight-addendum-id: cuando se llega desde el listado con ?addendaId=, la sección hace scroll
           y resalta esa adenda. -->
      <contract-addenda-section
        :contract-id="contract.id"
        :company-id="contract.company ? contract.company.id : null"
        :highlight-addendum-id="highlightAddendumId"
      />

      <!-- Documentos generados desde plantilla + versiones editadas -->
      <contract-template-variants-section
        v-if="contract.employee"
        :contract-id="contract.id"
        :employee-id="contract.employee.id"
      />

      <contract-documents-section :contract-id="contract.id" />

      <!-- Cese del contrato (solo si está cesado) -->
      <wz-card v-if="isCeased" title="Cese del contrato" class="mb-3">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Fecha de cese</small>
            <strong v-local-date="contract.terminationDate"></strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Tipo de cese</small>
            <strong>{{ contract.terminationType || '---' }}</strong>
          </div>
          <div class="w-full md:w-1/3 px-2 mb-3">
            <small class="text-muted block">Motivo</small>
            <strong>{{ contract.cessationReasonName || '---' }}</strong>
          </div>
          <div class="w-full px-2 mb-3">
            <small class="text-muted block">Observaciones</small>
            <strong>{{ contract.terminationNotes || '---' }}</strong>
          </div>
        </div>

        <!-- Documentos de cese -->
        <hr class="my-3" />
        <div class="flex justify-between items-center mb-3">
          <h6 class="mb-0 font-bold">Documentos de cese</h6>
          <div class="flex gap-2">
            <wz-button
              v-if="contract && contract.terminationDate"
              variant="outline"
              size="sm"
              @click="goToSeverance"
            >
              <i class="ri-file-add-line mr-1"></i>Generar liquidación
            </wz-button>
            <wz-button variant="primary" size="sm" @click="openCessationUploadModal">
              <i class="ri-upload-2-line mr-1"></i>Subir documento de cese
            </wz-button>
          </div>
        </div>

        <div v-if="!cessationDocuments.length" class="text-center py-3 text-muted">
          No hay documentos de cese registrados
        </div>
        <table v-else class="wz-table w-full">
          <thead>
            <tr>
              <th>Archivo</th>
              <th>Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="doc in cessationDocuments" :key="doc.id">
              <td>{{ doc.name }}</td>
              <td class="cell-center d_option md:pr-2 relative">
                <wz-button variant="outline" size="sm" @click="downloadCessationDoc(doc)">
                  <i class="ri-download-2-line mr-1"></i>Descargar
                </wz-button>
              </td>
            </tr>
          </tbody>
        </table>
      </wz-card>
    </template>

    <!-- Modal de subida de documento de cese -->
    <wz-modal
      v-model="showCessationModal"
      title="Subir documento de cese"
      @close="resetCessationForm"
    >
      <div class="mb-3">
        <label>Archivo * <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 10MB)</small></label>
        <wz-file-input
          :key="cessationFileKey"
          class="w-full mt-2"
          id-file="contract-cessation-doc-upload"
          :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
          @onChange="onCessationFileChange"
        />
      </div>

      <div class="flex justify-end mt-3">
        <wz-button variant="light" class="mr-2" @click="showCessationModal = false">Cancelar</wz-button>
        <wz-button variant="primary" @click="uploadCessationDoc" :disabled="cessationUploading">
          {{ cessationUploading ? 'Subiendo...' : 'Subir' }}
        </wz-button>
      </div>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { ContractsService } from '@/core/services'
import { Constants } from '@/core/utils'
import fileDownload from 'js-file-download'
import ContractAddendaSection from '@/modules/Contracts/Pages/components/ContractAddendaSection.vue'
import ContractDocumentsSection from '@/modules/Contracts/Pages/components/ContractDocumentsSection.vue'
import ContractTemplateVariantsSection from '@/modules/Contracts/Pages/components/ContractTemplateVariantsSection.vue'
import familyAllowanceLabelMixin from '@/core/mixins/familyAllowanceLabel'

export default {
  name: 'ContractDetail',
  mixins: [familyAllowanceLabelMixin],
  components: { ContractAddendaSection, ContractDocumentsSection, ContractTemplateVariantsSection },
  data() {
    return {
      contract: null,
      showCessationModal: false,
      cessationUploading: false,
      cessationFileKey: 0,
      cessationFile: null,
    }
  },
  computed: {
    cessationDocuments() {
      return (this.contract && this.contract.cessationDocuments) || []
    },
    isCeased() {
      if (!this.contract) return false
      return this.contract.displayStatus === 'CESADO'
    },
    statusBadge() {
      const ds = this.contract ? this.contract.displayStatus : null
      switch (ds) {
        case 'POR_INICIAR': return { label: 'POR INICIAR', color: 'info-solid' }
        case 'POR_VENCER':  return { label: 'POR VENCER',  color: 'warning-solid' }
        case 'VIGENTE':     return { label: 'VIGENTE',     color: 'success-solid' }
        case 'VENCIDO':     return { label: 'VENCIDO',     color: 'danger-solid' }
        case 'CESADO':      return { label: 'CESADO',      color: 'secondary-solid' }
        default:            return { label: '-',           color: 'dark' }
      }
    },
    contractTypeLabel() {
      const key = this.contract ? this.contract.contractType : null
      if (!key) return '---'
      const item = Object.values(Constants.ContractType).find(t => t.key === key)
      return item ? item.label : key
    },
    // Duración a mostrar (solo presentación). Prioriza el campo persistido durationMonths; si viene
    // null/0 pero el contrato es a plazo fijo (tiene finishDate), la deriva de startDate..finishDate.
    // < 1 mes => días; >= 1 mes => "N meses" o "N meses y M días" (resto exacto, SIN redondear).
    // Sin finishDate => Indeterminado.
    durationLabel() {
      const c = this.contract
      if (!c) return '---'
      if (c.durationMonths) return `${c.durationMonths} ${c.durationMonths === 1 ? 'mes' : 'meses'}`
      if (!c.startDate || !c.finishDate) return 'Indeterminado'
      const DAY = 86400000
      const start = new Date(`${c.startDate}T00:00:00`)
      const finish = new Date(`${c.finishDate}T00:00:00`) // último día inclusivo del contrato
      const end = new Date(finish.getTime())
      end.setDate(end.getDate() + 1) // fin exclusivo para contar meses completos (regla "día menos")
      const totalDays = Math.round((end - start) / DAY)
      if (totalDays <= 0) return 'Indeterminado'
      // Meses de calendario completos (mayor N con start + N meses <= fin exclusivo).
      let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
      if (end.getDate() < start.getDate()) months -= 1
      if (months < 1) return `${totalDays} ${totalDays === 1 ? 'día' : 'días'}`
      // Resto EXACTO en días respecto al mes completo N (sin redondear). <= 0 => N meses justos.
      const afterMonths = new Date(start.getTime())
      afterMonths.setMonth(afterMonths.getMonth() + months)
      const remainderDays = Math.round((finish - afterMonths) / DAY)
      const monthsPart = `${months} ${months === 1 ? 'mes' : 'meses'}`
      if (remainderDays <= 0) return monthsPart
      return `${monthsPart} y ${remainderDays} ${remainderDays === 1 ? 'día' : 'días'}`
    },
    employeeName() {
      const e = this.contract && this.contract.employee
      if (!e) return '---'
      return e.fullName || [e.lastNamePaternal, e.lastNameMaternal, e.name].filter(Boolean).join(' ') || '---'
    },
    workSiteName() {
      const ws = this.contract && this.contract.workSite
      if (!ws) return '---'
      return ws.name || ws.description || '---'
    },
    formatSalary() {
      const c = this.contract
      if (!c || c.baseSalary == null) return '---'
      const currency = c.salaryCurrency || 'PEN'
      return `${currency} ${Number(c.baseSalary).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`
    },
    // Adenda a resaltar al abrir el detalle desde el listado (?addendaId=). Number o null.
    highlightAddendumId() {
      const raw = this.$route.query.addendaId
      return raw != null && raw !== '' ? Number(raw) : null
    },
  },
  methods: {
    refName(obj, field) {
      return (obj && obj[field]) || '---'
    },
    // Navega a Auditoría con el filtro "ver por entidad" pre-cargado para este contrato.
    goToAuditHistory() {
      if (!this.contract) return
      this.$router.push({
        name: 'audit-log',
        query: { entityType: 'Contract', entityId: this.contract.id },
      })
    },
    boolLabel(val) {
      if (val === true || val === 1) return 'Sí'
      if (val === false || val === 0) return 'No'
      return '---'
    },
    async loadContract() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await ContractsService.get(this.$route.params.id)
        this.contract = resp.payload
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el contrato')
        this.$router.push({ name: 'contracts-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadCessationDoc(doc) {
      try {
        const response = await ContractsService.downloadCessationDocument(this.contract.id, doc.id)
        fileDownload(response.data, doc.name || 'documento-cese')
      } catch (e) {
        Alert.error('Error al descargar el documento de cese')
        console.error(e)
      }
    },
    openCessationUploadModal() {
      this.resetCessationForm()
      this.showCessationModal = true
    },
    goToSeverance() {
      this.$router.push({
        name: 'severance-calculate',
        query: {
          contractId: this.contract.id,
          companyId: this.contract.company ? this.contract.company.id : undefined,
        },
      })
    },
    resetCessationForm() {
      this.cessationFile = null
      this.cessationFileKey += 1 // re-monta el wz-file-input para limpiar el archivo
    },
    onCessationFileChange(files) {
      this.cessationFile = files && files.length > 0 ? files[0] : null
    },
    async uploadCessationDoc() {
      if (!this.cessationFile) {
        Alert.error('Selecciona un archivo')
        return
      }
      try {
        this.cessationUploading = true
        await ContractsService.uploadCessationDocument(this.contract.id, this.cessationFile)
        Alert.success('Documento de cese subido correctamente')
        this.showCessationModal = false
        await this.loadContract() // recargar para reflejar el nuevo documento
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al subir el documento de cese')
        console.error(e)
      } finally {
        this.cessationUploading = false
      }
    },
    // Navega al detalle del contrato origen (A) desde el banner de renovación.
    goToOriginContract() {
      if (!this.contract || !this.contract.renewFromId) return
      this.$router.push({ name: 'contracts-detail', params: { id: this.contract.renewFromId } })
    },
  },
  watch: {
    // Al navegar entre detalles de contrato (p. ej. "Ver contrato anterior") recargar los datos.
    '$route.params.id'() {
      this.loadContract()
    },
  },
  created() {
    this.loadContract()
  },
}
</script>

<style lang="stylus" scoped></style>
